import React, { useState } from 'react';
import { PageShell, StatCard } from '../../components/Ui';
import { MdStar, MdStarBorder, MdAdd, MdPeople, MdThumbUp, MdThumbDown } from 'react-icons/md';
import { useHmsRecords } from '../../hooks/useHmsRecords';

const DEPARTMENTS = ['OPD', 'IPD/Ward', 'Emergency', 'Lab', 'Pharmacy', 'Radiology', 'OT', 'Reception', 'Billing', 'Overall'];
const ASPECTS = ['Doctor Behavior', 'Nurse Behavior', 'Cleanliness', 'Food Quality', 'Wait Time', 'Billing Clarity', 'Overall Experience'];

function StarRating({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          style={{ background: 'none', border: 'none', cursor: onChange ? 'pointer' : 'default', padding: 2, color: star <= (value || 0) ? '#f59e0b' : '#e2e8f0', fontSize: 20 }}
          onClick={() => onChange && onChange(star)}
        >
          {star <= (value || 0) ? <MdStar /> : <MdStarBorder />}
        </button>
      ))}
    </div>
  );
}

function ratingToNps(rating5) {
  return Math.round((rating5 / 5) * 10);
}

function exportFeedbackCsv(feedbacks) {
  const rows = [['Date', 'Patient', 'UHID', 'Department', 'Doctor', 'Recommend', 'Overall Rating', 'NPS Score', 'Comment']];
  feedbacks.forEach(f => {
    const overallRating = f.ratings?.['Overall Experience'] || 0;
    rows.push([
      f.date || '',
      f.patient || '',
      f.uhid || '',
      f.department || '',
      f.doctor || '',
      f.recommend ? 'Yes' : 'No',
      overallRating,
      ratingToNps(overallRating),
      (f.comment || '').replace(/\n/g, ' '),
    ]);
  });
  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `PatientFeedback_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function PatientFeedbackPage() {
  const { records: feedbacks, loading, error, reload, create } = useHmsRecords('feedback');
  const { create: createIncident } = useHmsRecords('quality-incident');
  const [tab, setTab] = useState('summary');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ patient: '', uhid: '', department: 'OPD', doctor: '', comment: '', recommend: true, recommendScore: 7, ratings: {} });
  const [saving, setSaving] = useState(false);
  const [escalating, setEscalating] = useState(null);

  const setRating = (aspect, val) => setForm((f) => ({ ...f, ratings: { ...f.ratings, [aspect]: val } }));

  const addFeedback = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await create({
        patient: form.patient,
        uhid: form.uhid,
        department: form.department,
        doctor: form.doctor,
        comment: form.comment,
        recommend: form.recommend,
        recommendScore: Number(form.recommendScore),
        ratings: form.ratings,
        date: new Date().toISOString().slice(0, 10),
      });
      setShowForm(false);
      setForm({ patient: '', uhid: '', department: 'OPD', doctor: '', comment: '', recommend: true, recommendScore: 7, ratings: {} });
    } catch (ex) {
      alert(ex.message || 'Could not save feedback');
    } finally {
      setSaving(false);
    }
  };

  const escalate = async (fb) => {
    setEscalating(fb.id || fb.recordId);
    try {
      await createIncident({
        category: 'Complaint',
        title: `Patient complaint — ${fb.patient} (${fb.department})`,
        description: fb.comment || 'Low rating from patient feedback',
        patientName: fb.patient,
        uhid: fb.uhid,
        reportedAt: new Date().toISOString(),
        status: 'Open',
        severity: 'Medium',
        source: 'PatientFeedback',
        feedbackId: fb.recordId || fb.id,
      });
      window.alert('Quality incident created from feedback.');
    } catch (ex) {
      window.alert(ex.message || 'Could not escalate');
    } finally { setEscalating(null); }
  };

  const avgOverall = feedbacks.length ? Math.round(feedbacks.reduce((s, f) => s + (f.ratings?.['Overall Experience'] || 0), 0) / feedbacks.length * 10) / 10 : 0;
  const recommendPct = feedbacks.length ? Math.round(feedbacks.filter((f) => f.recommend).length / feedbacks.length * 100) : 0;

  const npsScores = feedbacks.map(f => {
    const score = f.recommendScore != null ? Number(f.recommendScore) : ratingToNps(f.ratings?.['Overall Experience'] || 0);
    return score;
  });
  const promoters = npsScores.filter(s => s >= 9).length;
  const detractors = npsScores.filter(s => s <= 6).length;
  const nps = feedbacks.length ? Math.round(((promoters - detractors) / feedbacks.length) * 100) : 0;

  const deptAvg = DEPARTMENTS.slice(0, -1).map((dept) => {
    const dFeedbacks = feedbacks.filter((f) => f.department === dept);
    const avg = dFeedbacks.length ? Math.round(dFeedbacks.reduce((s, f) => s + (f.ratings?.['Overall Experience'] || 0), 0) / dFeedbacks.length * 10) / 10 : null;
    return { dept, avg, count: dFeedbacks.length };
  }).filter((d) => d.count > 0);

  const aspectAvg = ASPECTS.map((aspect) => {
    const rated = feedbacks.filter((f) => f.ratings?.[aspect] != null);
    const avg = rated.length ? Math.round(rated.reduce((s, f) => s + (f.ratings[aspect] || 0), 0) / rated.length * 10) / 10 : null;
    return { aspect, avg, count: rated.length };
  }).filter((a) => a.count > 0);

  const isLowRating = (fb) => {
    const overallRating = fb.ratings?.['Overall Experience'];
    return !fb.recommend || (overallRating != null && overallRating <= 2) || (fb.recommendScore != null && Number(fb.recommendScore) <= 6);
  };

  return (
    <PageShell
      title="Patient Feedback & Satisfaction"
      subtitle="Real-time patient satisfaction monitoring · NPS tracking · Department scores"
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => exportFeedbackCsv(feedbacks)}>📊 Export CSV</button>
          <button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}><MdAdd /> Collect Feedback</button>
        </div>
      }
    >
      {error && <div className="error mb">{error} <button type="button" className="btn btn-sm btn-secondary" onClick={reload}>Retry</button></div>}
      {loading && <p className="hint mb">Loading feedback…</p>}

      <div className="stat-grid">
        <StatCard label="Total Feedback" value={feedbacks.length} accent="#8b5cf6" icon={MdPeople} />
        <StatCard label="Avg Rating" value={`${avgOverall}/5`} accent="#f59e0b" icon={MdStar} />
        <StatCard label="Would Recommend" value={`${recommendPct}%`} accent="#10b981" icon={MdThumbUp} />
        <StatCard label="Negative Feedback" value={feedbacks.filter((f) => !f.recommend).length} accent="#ef4444" icon={MdThumbDown} />
      </div>

      <div className="card mb" style={{ borderLeft: '4px solid #6366f1', padding: 16 }}>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>Net Promoter Score (NPS)</div>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 40, fontWeight: 900, color: nps >= 50 ? '#10b981' : nps >= 0 ? '#f59e0b' : '#dc2626' }}>{nps >= 0 ? '+' : ''}{nps}</div>
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            <div>Promoters (9–10): <strong style={{ color: '#10b981' }}>{promoters}</strong></div>
            <div>Passives (7–8): <strong style={{ color: '#f59e0b' }}>{npsScores.filter(s => s >= 7 && s <= 8).length}</strong></div>
            <div>Detractors (0–6): <strong style={{ color: '#dc2626' }}>{detractors}</strong></div>
          </div>
        </div>
      </div>

      {showForm && (
        <form className="card mb" onSubmit={addFeedback}>
          <h3 style={{ marginTop: 0 }}>Collect Patient Feedback</h3>
          <div className="form-grid">
            <div className="field"><label>Patient name *</label><input className="input" required value={form.patient} onChange={(e) => setForm({ ...form, patient: e.target.value })} /></div>
            <div className="field"><label>UHID (optional)</label><input className="input" value={form.uhid} onChange={(e) => setForm({ ...form, uhid: e.target.value })} placeholder="Hospital UHID" /></div>
            <div className="field"><label>Department</label>
              <select className="input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                {DEPARTMENTS.slice(0, -1).map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div className="field"><label>Doctor (if applicable)</label><input className="input" value={form.doctor} onChange={(e) => setForm({ ...form, doctor: e.target.value })} /></div>
            <div className="field"><label>Recommend Score (0–10)</label><input type="number" min={0} max={10} className="input" value={form.recommendScore} onChange={(e) => setForm({ ...form, recommendScore: e.target.value })} /></div>
          </div>
          <div style={{ margin: '14px 0' }}>
            <div className="rx-section-title">Ratings</div>
            <div className="fb-ratings-grid">
              {ASPECTS.map((aspect) => (
                <div key={aspect} className="fb-rating-row">
                  <span className="fb-aspect-label">{aspect}</span>
                  <StarRating value={form.ratings[aspect]} onChange={(v) => setRating(aspect, v)} />
                </div>
              ))}
            </div>
          </div>
          <div className="field full">
            <label>Comments / Suggestions</label>
            <textarea className="input" rows={3} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Patient's feedback and suggestions..." />
          </div>
          <div className="fb-recommend-row">
            <span style={{ fontWeight: 600, fontSize: 13 }}>Would you recommend us?</span>
            <button type="button" className="att-status-btn" style={{ background: form.recommend ? '#10b981' : '#f1f5f9', color: form.recommend ? '#fff' : '#64748b' }} onClick={() => setForm({ ...form, recommend: true })}>👍 Yes</button>
            <button type="button" className="att-status-btn" style={{ background: !form.recommend ? '#ef4444' : '#f1f5f9', color: !form.recommend ? '#fff' : '#64748b' }} onClick={() => setForm({ ...form, recommend: false })}>👎 No</button>
          </div>
          <div className="form-actions" style={{ marginTop: 14 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Submit Feedback'}</button>
          </div>
        </form>
      )}

      <div className="period-tabs card mb">
        {[
          { id: 'summary', label: '📊 Summary & Analytics' },
          { id: 'responses', label: `💬 All Responses (${feedbacks.length})` },
        ].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {tab === 'summary' && (
        <>
          <div className="fb-score-hero">
            <div className="fb-score-num">{avgOverall}</div>
            <div className="fb-score-stars"><StarRating value={Math.round(avgOverall)} /></div>
            <div className="fb-score-label">Overall Patient Satisfaction</div>
            <div className="fb-recommend-pct">
              <span style={{ color: '#10b981', fontWeight: 800, fontSize: 24 }}>{recommendPct}%</span>
              <span style={{ color: '#64748b', fontSize: 13 }}>would recommend</span>
            </div>
          </div>

          <div className="card mb">
            <h3>Aspect-wise Score</h3>
            {aspectAvg.length === 0 && <p className="hint">Collect feedback to see analytics.</p>}
            <div className="fb-aspect-bars">
              {aspectAvg.map(({ aspect, avg }) => (
                <div key={aspect} className="fb-aspect-bar-row">
                  <span className="fb-aspect-name">{aspect}</span>
                  <div className="fb-bar-bg">
                    <div className="fb-bar-fill" style={{ width: `${(avg / 5) * 100}%`, background: avg >= 4 ? '#10b981' : avg >= 3 ? '#f59e0b' : '#ef4444' }} />
                  </div>
                  <span className="fb-bar-val" style={{ color: avg >= 4 ? '#10b981' : avg >= 3 ? '#ca8a04' : '#ef4444' }}>{avg}/5</span>
                </div>
              ))}
            </div>
          </div>

          {deptAvg.length > 0 && (
            <div className="card mb">
              <h3>Department-wise Satisfaction</h3>
              <div className="dash-dept-row">
                {deptAvg.map(({ dept, avg, count }) => (
                  <div key={dept} className="dash-dept-card">
                    <div className="dash-dept-val" style={{ color: avg >= 4 ? '#10b981' : avg >= 3 ? '#ca8a04' : '#ef4444' }}>{avg || '—'}</div>
                    <div className="dash-dept-label">{dept}</div>
                    <div className="dash-dept-sub">{count} feedback{count > 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'responses' && (
        <div className="card">
          <h3>All Patient Responses</h3>
          {feedbacks.length === 0 && <p className="hint table-empty">No feedback collected yet.</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {feedbacks.map((f) => (
              <div key={f.id} className="fb-response-card" style={{ borderLeft: isLowRating(f) ? '4px solid #ef4444' : undefined }}>
                <div className="fb-response-head">
                  <div>
                    <div className="fb-response-patient">{f.patient}</div>
                    {f.uhid && <div style={{ fontSize: 11, color: '#94a3b8' }}>UHID: {f.uhid}</div>}
                    <div className="fb-response-meta">{f.department}{f.doctor && f.doctor !== '—' ? ` · ${f.doctor}` : ''} · {f.date}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ marginBottom: 4 }}><StarRating value={f.ratings?.['Overall Experience'] || 0} /></div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: f.recommend ? '#10b981' : '#ef4444' }}>
                      {f.recommend ? '👍 Recommends' : '👎 Does not recommend'}
                    </span>
                    {f.recommendScore != null && <div style={{ fontSize: 11, color: '#64748b' }}>Score: {f.recommendScore}/10</div>}
                  </div>
                </div>
                {f.comment && <div className="fb-comment">"{f.comment}"</div>}
                <div className="fb-aspects-mini">
                  {Object.entries(f.ratings || {}).map(([aspect, rating]) => (
                    <span key={aspect} className="fb-mini-chip">
                      {aspect}: <strong style={{ color: rating >= 4 ? '#10b981' : rating >= 3 ? '#ca8a04' : '#ef4444' }}>{rating}★</strong>
                    </span>
                  ))}
                </div>
                {isLowRating(f) && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', fontSize: 11 }}
                      disabled={escalating === (f.id || f.recordId)}
                      onClick={() => escalate(f)}
                    >
                      {escalating === (f.id || f.recordId) ? 'Escalating…' : '⚠ Escalate as Complaint'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </PageShell>
  );
}
